import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDownIcon, Edit2Icon, CheckIcon, XIcon } from 'lucide-react';
import Dropdown from '../components/common/Dropdown';
import TimeframeFilter from '../components/common/TimeframeFilter';
import ProgressCircle from '../components/Charts/ProgressCircle';
import BarChart from '../components/Charts/BarChart';
import StackedBarChart from '../components/Charts/StackedBarChart';
import MedalChart from '../components/Charts/MedalChart';
import PieChart from '../components/Charts/PieChart';
import EnhancedStackedBarChart from '../components/Charts/EnhancedStackedBarChart';
import { toast } from 'react-toastify';
import PersonalizedFeedback from './PersonalizedFeedback';
import {
  useSaveWeeklyGoalMutation,
  useGetWeeklyGoalQuery,
  useGetCourseLanguageDistributionQuery,
  useGetDashboardGraphQuery,
} from '../redux/apiSlice';

interface ButtonProps {
  children: React.ReactNode;
  variant?: 'default' | 'outline';
  onClick: () => void;
  className?: string;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'default',
  onClick,
  className = '',
}) => {
  const baseClasses =
    'text-[18px]  px-4 py-2 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors';
  const variantClasses =
    variant === 'default'
      ? 'bg-[#023f81] text-white'
      : 'bg-white text-blue-600 ';

  return (
    <button
      className={`${baseClasses} ${variantClasses} ${className}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
}

const Select: React.FC<SelectProps> = ({ id, value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <Button
        className="w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        onClick={() => setIsOpen(!isOpen)}
      >
        {options.find((option) => option.value === value)?.label}
        <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
      </Button>
      {isOpen && (
        <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          {options.map((option) => (
            <li
              key={option.value}
              className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

interface CardProps {
  title: string;
  children: React.ReactNode;
}

const Card: React.FC<CardProps> = ({ title, children }) => {
  return (
    <div className="flex flex-col justify-between bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg md:text-xl font-semibold leading-6 text-gray-900">
          {title}
        </h3>
      </div>
      <div className="px-4 py-5 sm:p-6">{children}</div>
    </div>
  );
};

// Utility function for date formatting
const formatDate = (date: Date): string => {
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  return `${monthNames[date.getMonth()]} ${date.getDate()}`;
};

// Utility function to generate dates for the past 6 weeks
const getPastWeekDates = (count = 6): Date[] => {
  const dates: Date[] = [];
  const today = new Date();

  for (let i = count - 1; i >= 0; i--) {
    const date = new Date();
    date.setDate(today.getDate() - i * 7);
    dates.push(date);
  }

  return dates;
};

// Custom hook for window resize handling
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

const ProgressReports: React.FC<{ timeframe: string }> = ({ timeframe }) => {
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [editGoalValue, setEditGoalValue] = useState<number>(60);
  const [isUpdating, setIsUpdating] = useState(false);

  // Add the new API hooks
  const [saveWeeklyGoal] = useSaveWeeklyGoalMutation();
  const { data: weeklyGoalData, refetch: refetchWeeklyGoal } =
    useGetWeeklyGoalQuery();
  const { data: languageDistributionData, isLoading: isLanguageDataLoading } =
    useGetCourseLanguageDistributionQuery();
  const { data: dashboardGraphData, isLoading: isDashboardGraphLoading } =
    useGetDashboardGraphQuery({
      interval:
        timeframe === 'weekly'
          ? 'weekly'
          : timeframe === 'monthly'
            ? 'monthly'
            : 'annually',
    });

  // Transform dashboard graph data for charts
  const transformedGraphData = useMemo(() => {
    if (!dashboardGraphData)
      return { coursesByLevel: [], coursesByLanguage: [], medalCount: [] };

    const { language_level_graph, medal_count_graph } = dashboardGraphData;

    // Transform courses by level data
    const coursesByLevel = Object.entries(language_level_graph).map(
      ([label, data]) => {
        const segments = [
          {
            value: data.by_level?.beginner ?? 0,
            color: '#4195fb',
            label: 'Beginner',
          },
          {
            value: data.by_level?.intermediate ?? 0,
            color: '#7950db',
            label: 'Intermediate',
          },
          {
            value: data.by_level?.advanced ?? 0,
            color: '#4ade80',
            label: 'Advanced',
          },
        ];

        return {
          label,
          segments,
          total: segments.reduce((sum, segment) => sum + segment.value, 0),
        };
      },
    );

    // Helper function to get color for a language
    const getLanguageColor = (language: string): string => {
      const colorMap: Record<string, string> = {
        python: '#4CAF50',
        javascript: '#2196F3',
        'html/css': '#FFC107',
        sql: '#FF5722',
        typing: '#9C27B0',
        r: '#795548',
        default: '#607D8B',
      };
      return colorMap[language.toLowerCase()] || colorMap.default;
    };

    // List of all languages to always show in the chart
    const ALL_LANGUAGES = [
      'python',
      'javascript',
      'html/css',
      'sql',
      'typing',
      'r',
    ];

    // Transform courses by language data
    const coursesByLanguage = Object.entries(language_level_graph).map(
      ([label, data]) => {
        const segments = ALL_LANGUAGES.map((lang) => ({
          value: data.by_language?.[lang] ?? 0,
          color: getLanguageColor(lang),
          label:
            lang.charAt(0).toUpperCase() + lang.slice(1).replace(/_/g, ' '),
        }));

        return {
          label,
          segments,
          total: segments.reduce((sum, segment) => sum + segment.value, 0),
        };
      },
    );

    // Transform medal count data
    const medalCount = Object.entries(medal_count_graph).map(
      ([label, data]) => ({
        label,
        segments: [
          { value: data?.gold ?? 0, color: '#FFD700', label: 'Gold' },
          { value: data?.silver ?? 0, color: '#C0C0C0', label: 'Silver' },
          { value: data?.bronze ?? 0, color: '#CD7F32', label: 'Bronze' },
        ],
        total: (data?.gold ?? 0) + (data?.silver ?? 0) + (data?.bronze ?? 0),
      }),
    );

    return { coursesByLevel, coursesByLanguage, medalCount };
  }, [dashboardGraphData]);

  // Initialize targetProgress with data from API
  const [targetProgress, setTargetProgress] = useState({
    progress: 0,
    goal: 60,
    unit: 'minutes',
  });

  // Update targetProgress when API data changes
  useEffect(() => {
    if (weeklyGoalData) {
      setTargetProgress({
        progress: weeklyGoalData.total_time_spent || 0,
        goal: weeklyGoalData?.target_time || 60,
        unit: 'minutes',
      });
      setEditGoalValue(weeklyGoalData.target_time);
    }
  }, [weeklyGoalData]);

  // Transform language distribution data for the pie chart
  const transformedLanguageData = useMemo(() => {
    type LanguageKey =
      | 'python'
      | 'javascript'
      | 'html/css'
      | 'sql'
      | 'typing'
      | 'r';

    const defaultLanguages: Record<
      LanguageKey,
      { label: string; value: number; color: string }
    > = {
      python: { label: 'Python', value: 0, color: '#4CAF50' },
      javascript: { label: 'JavaScript', value: 0, color: '#2196F3' },
      'html/css': { label: 'Html/Css', value: 0, color: '#FFC107' },
      sql: { label: 'SQL', value: 0, color: '#FF5722' },
      typing: { label: 'Typing', value: 0, color: '#9C27B0' },
      r: { label: 'R', value: 0, color: '#795548' },
    };

    // Update values from API data
    languageDistributionData?.data.forEach((item) => {
      const lang = item.language__name.toLowerCase() as LanguageKey;
      if (lang in defaultLanguages) {
        defaultLanguages[lang].value = item.course_count;
      }
    });

    return Object.values(defaultLanguages);
  }, [languageDistributionData]);

  // Helper function to get color based on language
  const getLanguageColor = (language: string) => {
    const colorMap: Record<string, string> = {
      python: '#4CAF50',
      javascript: '#2196F3',
      'html/css': '#FFC107',
      sql: '#FF5722',
      typing: '#9C27B0',
      r: '#795548',
    };
    return colorMap[language] || '#607D8B';
  };

  // Generate data based on the selected timeframe
  const getTimeframeDates = () => {
    switch (timeframe) {
      case 'daily':
        // Last 7 days
        return Array.from({ length: 7 }, (_, i) => {
          const date = new Date();
          date.setDate(date.getDate() - (6 - i));
          return date;
        });
      case 'weekly':
        // Last 6 weeks
        return getPastWeekDates(6);
      case 'monthly':
        // Last 6 months
        return Array.from({ length: 6 }, (_, i) => {
          const date = new Date();
          date.setMonth(date.getMonth() - (5 - i));
          return date;
        });
      case 'yearly':
        // Last 6 years
        return Array.from({ length: 6 }, (_, i) => {
          const date = new Date();
          date.setFullYear(date.getFullYear() - (5 - i));
          return date;
        });
      default:
        return getPastWeekDates(6);
    }
  };

  const formatTimeframeDate = (date: Date): string => {
    switch (timeframe) {
      case 'daily':
        return formatDate(date);
      case 'weekly':
        return formatDate(date);
      case 'monthly':
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
      case 'yearly':
        return date.getFullYear().toString();
      default:
        return formatDate(date);
    }
  };

  // Get proper dates for the current timeframe
  const timeframeDates = getTimeframeDates();

  // Sample data for language usage (per day of the week)
  const weekDays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const languageUsageData = weekDays.map((day, index) => {
    // Generate data only for weekdays (for realism)
    if (index < 5) {
      // Generate random values for each language
      const pythonValue = Math.floor(Math.random() * 5) + 1;
      const jsValue = Math.floor(Math.random() * 4) + 1;
      const sqlValue = Math.floor(Math.random() * 3) + 1;

      return {
        label: day,
        segments: [
          { value: pythonValue, color: '#4CAF50', label: 'Python' },
          { value: jsValue, color: '#2196F3', label: 'JavaScript' },
          { value: sqlValue, color: '#FFC107', label: 'SQL' },
        ],
        total: pythonValue + jsValue + sqlValue,
      };
    } else {
      // For weekend days, show empty data
      return {
        label: day,
        segments: [],
        total: 0,
      };
    }
  });

  // Replace languageUsageData with medalCountData based on timeframeDates
  const medalCountData = [
    {
      label: '04/20/2023',
      segments: [
        { value: 12, color: '#FFD700', label: 'Gold' },
        { value: 4, color: '#C0C0C0', label: 'Silver' },
        { value: 8, color: '#CD7F32', label: 'Bronze' },
      ],
      total: 24,
    },
    {
      label: '04/27/2023',
      segments: [
        { value: 15, color: '#FFD700', label: 'Gold' },
        { value: 9, color: '#C0C0C0', label: 'Silver' },
        { value: 6, color: '#CD7F32', label: 'Bronze' },
      ],
      total: 30,
    },
    {
      label: '05/04/2023',
      segments: [
        { value: 18, color: '#FFD700', label: 'Gold' },
        { value: 12, color: '#C0C0C0', label: 'Silver' },
        { value: 7, color: '#CD7F32', label: 'Bronze' },
      ],
      total: 37,
    },
    {
      label: '05/11/2023',
      segments: [
        { value: 10, color: '#FFD700', label: 'Gold' },
        { value: 7, color: '#C0C0C0', label: 'Silver' },
        { value: 4, color: '#CD7F32', label: 'Bronze' },
      ],
      total: 21,
    },
    {
      label: '05/18/2023',
      segments: [
        { value: 14, color: '#FFD700', label: 'Gold' },
        { value: 10, color: '#C0C0C0', label: 'Silver' },
        { value: 6, color: '#CD7F32', label: 'Bronze' },
      ],
      total: 30,
    },
    {
      label: '05/25/2023',
      segments: [
        { value: 17, color: '#FFD700', label: 'Gold' },
        { value: 11, color: '#C0C0C0', label: 'Silver' },
        { value: 8, color: '#CD7F32', label: 'Bronze' },
      ],
      total: 36,
    },
    {
      label: '06/01/2023',
      segments: [
        { value: 13, color: '#FFD700', label: 'Gold' },
        { value: 9, color: '#C0C0C0', label: 'Silver' },
        { value: 6, color: '#CD7F32', label: 'Bronze' },
      ],
      total: 28,
    },
  ];

  // Adjust medal data based on timeframe (for demo purposes)
  const getTimeframeMultiplier = () => {
    switch (timeframe) {
      case 'daily':
        return 0.2;
      case 'weekly':
        return 1;
      case 'monthly':
        return 4;
      case 'yearly':
        return 48;
      default:
        return 1;
    }
  };

  const medalMultiplier = getTimeframeMultiplier();

  // Sample data for medal count
  const medalData = [
    {
      language: 'Python',
      gold: Math.floor(3 * medalMultiplier),
      silver: Math.floor(5 * medalMultiplier),
      bronze: Math.floor(15 * medalMultiplier),
    },
    {
      language: 'R',
      gold: Math.floor(1 * medalMultiplier),
      silver: Math.floor(2 * medalMultiplier),
      bronze: Math.floor(6 * medalMultiplier),
    },
    {
      language: 'JavaScript',
      gold: Math.floor(1 * medalMultiplier),
      silver: Math.floor(1 * medalMultiplier),
      bronze: Math.floor(2 * medalMultiplier),
    },
    {
      language: 'SQL',
      gold: Math.floor(1 * medalMultiplier),
      silver: Math.floor(1 * medalMultiplier),
      bronze: Math.floor(1 * medalMultiplier),
    },
  ];

  // Generate data for courses completed by level
  const coursesByLevelData = [
    {
      label: '4/6/2023',
      segments: [
        { value: 30, color: '#4195fb', label: 'Beginner' },
        { value: 22, color: '#7950db', label: 'Intermediate' },
        { value: 8, color: '#4ade80', label: 'Advanced' },
      ],
      total: 60,
    },
    {
      label: '4/20/2023',
      segments: [
        { value: 50, color: '#4195fb', label: 'Beginner' },
        { value: 30, color: '#7950db', label: 'Intermediate' },
        { value: 18, color: '#4ade80', label: 'Advanced' },
      ],
      total: 98,
    },
    {
      label: '4/27/2023',
      segments: [
        { value: 40, color: '#4195fb', label: 'Beginner' },
        { value: 30, color: '#7950db', label: 'Intermediate' },
        { value: 13, color: '#4ade80', label: 'Advanced' },
      ],
      total: 83,
    },
    {
      label: '5/4/2023',
      segments: [
        { value: 20, color: '#4195fb', label: 'Beginner' },
        { value: 10, color: '#7950db', label: 'Intermediate' },
        { value: 10, color: '#4ade80', label: 'Advanced' },
      ],
      total: 40,
    },
    {
      label: '5/11/2023',
      segments: [
        { value: 30, color: '#4195fb', label: 'Beginner' },
        { value: 22, color: '#7950db', label: 'Intermediate' },
        { value: 8, color: '#4ade80', label: 'Advanced' },
      ],
      total: 60,
    },
  ];

  // Generate data for courses completed by language
  const coursesByLanguageData = [
    {
      label: '4/6/2023',
      segments: [
        { value: 25, color: '#4CAF50', label: 'Python' },
        { value: 20, color: '#2196F3', label: 'JavaScript' },
        { value: 15, color: '#FFC107', label: 'SQL' },
      ],
      total: 60,
    },
    {
      label: '4/20/2023',
      segments: [
        { value: 40, color: '#4CAF50', label: 'Python' },
        { value: 35, color: '#2196F3', label: 'JavaScript' },
        { value: 23, color: '#FFC107', label: 'SQL' },
      ],
      total: 98,
    },
    {
      label: '4/27/2023',
      segments: [
        { value: 35, color: '#4CAF50', label: 'Python' },
        { value: 28, color: '#2196F3', label: 'JavaScript' },
        { value: 15, color: '#FFC107', label: 'SQL' },
      ],
      total: 78,
    },
    {
      label: '5/4/2023',
      segments: [
        { value: 18, color: '#4CAF50', label: 'Python' },
        { value: 12, color: '#2196F3', label: 'JavaScript' },
        { value: 10, color: '#FFC107', label: 'SQL' },
      ],
      total: 40,
    },
    {
      label: '5/11/2023',
      segments: [
        { value: 25, color: '#4CAF50', label: 'Python' },
        { value: 22, color: '#2196F3', label: 'JavaScript' },
        { value: 13, color: '#FFC107', label: 'SQL' },
      ],
      total: 60,
    },
  ];

  const updateWeeklyGoal = async () => {
    try {
      // Validate the goal value
      if (editGoalValue <= 0) {
        toast.error('Goal must be greater than zero');
        return;
      }

      if (editGoalValue > 10080) {
        toast.error('Goal cannot exceed 10,080 minutes (1 week)');
        return;
      }

      setIsUpdating(true);

      // Call the API to save the weekly goal
      await saveWeeklyGoal({ target: editGoalValue });

      // Refetch the weekly goal data
      await refetchWeeklyGoal();

      setIsEditingGoal(false);
      setIsUpdating(false);
      toast.success('Weekly goal updated successfully');
    } catch (error) {
      console.error('Error updating weekly goal:', error);
      toast.error('Failed to update weekly goal');
      setIsUpdating(false);
    }
  };

  // Reset editing state
  const cancelEditing = () => {
    const savedGoal = localStorage.getItem('weeklyGoal');
    if (savedGoal) {
      setEditGoalValue(parseInt(savedGoal));
    }
    setIsEditingGoal(false);
  };

  // Generate data for courses completed
  const coursesCompletedData = timeframeDates.map((date, index) => {
    // Generate a random value based on the timeframe
    let maxValue = 30;
    if (timeframe === 'monthly') maxValue = 120;
    if (timeframe === 'yearly') maxValue = 500;

    const value = Math.floor(Math.random() * maxValue) + maxValue / 2;
    const dateStr = formatTimeframeDate(date);

    return {
      label: dateStr,
      value,
      color: '#ff5722',
      date: dateStr,
    };
  });

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
        {/* Target Progress */}
        <Card title={`Weekly Target Progress`}>
          <div className="flex flex-col items-center p-2 mb-8">
            <div className="flex items-center mb-2 md:mb-4">
              <h4 className="text-lg md:text-xl font-semibold">
                Weekly Target Goal:
                {isEditingGoal ? (
                  <div className="inline-flex items-center ml-2">
                    <input
                      type="number"
                      value={editGoalValue}
                      onChange={(e) =>
                        setEditGoalValue(parseInt(e.target.value) || 0)
                      }
                      className="w-16 border rounded px-2 py-1 text-center"
                      min="1"
                      max="500"
                    />
                    <span className="ml-1 text-blue-600">
                      {targetProgress.unit}
                    </span>
                    <button
                      onClick={updateWeeklyGoal}
                      disabled={isUpdating}
                      className="ml-2 p-1 text-green-600 hover:bg-green-100 rounded"
                    >
                      <CheckIcon size={16} />
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="ml-1 p-1 text-red-600 hover:bg-red-100 rounded"
                    >
                      <XIcon size={16} />
                    </button>
                  </div>
                ) : (
                  <>
                    <span className="ml-1 text-blue-600">
                      {targetProgress.goal} {targetProgress.unit}
                    </span>
                    <button
                      onClick={() => setIsEditingGoal(true)}
                      className="ml-2 p-1 text-blue-600 hover:bg-blue-100 rounded"
                    >
                      <Edit2Icon size={16} />
                    </button>
                  </>
                )}
              </h4>
            </div>
            <div className="w-full max-w-xs">
              <ProgressCircle
                progress={targetProgress.progress}
                goal={targetProgress.goal}
                unit={targetProgress.unit}
                size={isMobile ? 160 : 200}
              />
            </div>
          </div>
        </Card>

        {/* Language Distribution */}
        <Card title="Total Number of Activities Completed By Language Since Joining My Code Dragon">
          <div className="flex flex-col items-center p-2">
            {isLanguageDataLoading ? (
              <div className="flex items-center justify-center h-[220px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : transformedLanguageData.length === 0 ? (
              <div className="flex items-center justify-center h-[220px] text-gray-500">
                No language data available yet
              </div>
            ) : (
              <PieChart
                data={transformedLanguageData}
                size={isMobile ? 180 : 220}
                className="flex-grow"
              />
            )}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
        {/* Courses Completed */}
        <Card
          title={`Courses Completed ${timeframe.charAt(0).toUpperCase() + timeframe.slice(1)}`}
        >
          <div className="p-2 h-full flex flex-col">
            {isDashboardGraphLoading ? (
              <div className="flex items-center justify-center h-[300px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <EnhancedStackedBarChart
                data={transformedGraphData.coursesByLevel}
                alternativeData={transformedGraphData.coursesByLanguage}
                height={isMobile ? 250 : 300}
                className="flex-grow"
                showYAxis={true}
                showValue={false}
                yAxisStep={20}
              />
            )}
          </div>
        </Card>

        {/* Medal Count Progress */}
        <Card
          title={`${timeframe.charAt(0).toUpperCase() + timeframe.slice(1)} Medal Count Progress`}
        >
          <div className="p-2 h-full flex flex-col">
            {isDashboardGraphLoading ? (
              <div className="flex items-center justify-center h-[300px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <StackedBarChart
                data={transformedGraphData.medalCount}
                height={isMobile ? 250 : 350}
                className="flex-grow"
                title=""
                showYAxis={true}
                yAxisStep={10}
                showValue={false}
              />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

// const PersonalizedFeedback: React.FC = () => {
//   return (
//     <Card title="Personalized Feedback">
//       <h3 className="text-lg md:text-xl font-semibold mb-3">
//         1. Skill Strengths and Areas for Improvement:
//       </h3>
//       <p className="mb-4">
//         Identify areas where the student excels, based on their performance in
//         coding exercises. Also highlight improvement areas where the student has
//         struggled or needs more practice, with suggestions for improvement.
//       </p>

//       <h3 className="text-lg md:text-xl font-semibold mb-3">
//         2. Coding Accuracy:
//       </h3>
//       <p className="mb-4">
//         Error Rate: Display the percentage of errors or bugs in the
//         student&apos;s submissions. Reflect this in a chart; it also reflects
//         Attempt vs Error Rate by Default it reflects all sections but have a
//         global filter for this chart and AI Usage for the student to select a
//         section in the course.
//       </p>

//       <h3 className="text-lg md:text-xl font-semibold mb-3">
//         3. My Code Dragon AI Usage:
//       </h3>
//       <p className="mb-4">
//         AI Assistance Usage: Track how often the student used My Code Dragon for
//         help and whether they successfully implemented the AI&apos;s
//         suggestions. Reflect this in a chart; it also reflects Attempt vs Usage
//         by Default it reflects all sections but have a global filter for this
//         chart and AI Usage for the student to select a section in the course.
//       </p>

//       <h3 className="text-lg md:text-xl font-semibold mb-3">
//         4. Learning Progress:
//       </h3>
//       <p className="mb-3">
//         Progress Toward Goals: Show how the student&apos;s current performance
//         aligns with their learning objectives or course goals, including overall
//         progress achieved.
//       </p>
//       <p className="mb-3">
//         Suggested Next Steps: Offer personalized course recommendations to help
//         the student reach the next level of their learning journey.
//       </p>
//       <p>
//         Time Management: Display the average time spent on assignments,
//         comparing it to the expected time for each task.
//       </p>
//     </Card>
//   );
// };

const StudentDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'progress' | 'feedback'>(
    'progress',
  );
  const [selectedCourse, setSelectedCourse] = useState('my-courses');
  const [timeframeFilter, setTimeframeFilter] = useState('weekly');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    console.log(e.target, 'target');
  };

  const timeframeOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
  ];

  const courses = [
    { value: 'My Courses', label: 'My Courses' },
    { value: 'Weekly', label: 'Weekly' },
  ];

  return (
    <div className="flex flex-col h-full min-h-[80vh]">
      <div className="container mx-auto p-4 flex-grow flex flex-col">
        <div className="flex flex-wrap gap-2 mb-4">
          <Button
            variant={activeTab === 'progress' ? 'default' : 'outline'}
            onClick={() => setActiveTab('progress')}
          >
            My Progress Reports
          </Button>
          <Button
            variant={activeTab === 'feedback' ? 'default' : 'outline'}
            onClick={() => setActiveTab('feedback')}
          >
            Personalized Feedback
          </Button>
        </div>

        {activeTab === 'progress' ? (
          <>
            <ProgressReports timeframe={timeframeFilter} />
            <div className="mb-4 flex flex-wrap items-center justify-center gap-4 mt-4">
              <TimeframeFilter
                value={timeframeFilter}
                onChange={setTimeframeFilter}
                options={timeframeOptions}
                label="Timeframe Filter:"
              />
            </div>
          </>
        ) : (
          <PersonalizedFeedback />
        )}
      </div>
    </div>
  );
};

export default StudentDashboard;
